import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { loadStripe } from '@stripe/stripe-js'

import I18n from 'src/components/I18n'
import useModalLock from 'src/widgets/useModalLock'
import { paypalCurrencySupported } from 'src/payments/paypalCurrency'

import Panel from './Panel'
import StripeWrapper from './StripeWrapper'
import { HeadingBadgeProvider } from './PageHeader'
import DiscoveryECE from './DiscoveryECE'
import ProcessingOverlay from './ProcessingOverlay'
import Goal from './goal/Goal'
import Start from './steps/Start'
import Upsell from './steps/Upsell'
import PaymentMethodStep from './steps/PaymentMethod'
import ContactStep from './steps/Contact'
import StripePayment from './steps/StripePayment'
import BlackbaudPayment from './steps/BlackbaudPayment'
import ExpressCheckout from './steps/ExpressCheckout'
import PaypalPayment from './steps/PaypalPayment'
import DeferredPayment from './steps/DeferredPayment'
import CryptoForm from './steps/CryptoForm'
import CryptoInfo from './steps/CryptoInfo'
import StockPayment from './steps/StockPayment'
import DafPayment from './steps/DafPayment'
import ThankYou from './steps/ThankYou'
import useDonation from './useDonation'
import useRecord from './useRecord'
import usePlugins from './usePlugins'
import useCrypto from './useCrypto'
import useDonationExtras from './useDonationExtras'
import useMonthlyUpsell from './useMonthlyUpsell'
import useCanadianReceipt from './useCanadianReceipt'
import useGiftAid from './useGiftAid'
import useDeferredPayment from './useDeferredPayment'
import usePaypal from './usePaypal'
import usePaypalSdk from './usePaypalSdk'
import useVenmo from './useVenmo'
import useBlackbaudCheckout from './useBlackbaudCheckout'
import useSimulatedDonation from './useSimulatedDonation'
import useDonationCompletion from './useDonationCompletion'
import useBeneficiarySelect from './useBeneficiarySelect'
import useExpressCheckoutDiscovery from './useExpressCheckoutDiscovery'
import useAbandonedCart from '../donate_v6/useAbandonedCart'
import useEmployerMatching from '../donate_v6/useEmployerMatching'
import { filterPaymentMethods } from './paymentMethodConfig'
import parseTipping from './parseTipping'
import proposeMonthlyUpsell from './proposeMonthlyUpsell'

const PAYPAL_METHODS = [ 'paypal', 'venmo' ]
const STRIPE_METHODS = [ 'credit_card', 'bank' ]
const WALLET_METHODS = [ 'apple_pay', 'google_pay' ]
const DEFERRED_METHODS = [ 'wire', 'check', 'pledge' ]

const App = ({
  amount, currencies, presets, min_amount, blank_amount,
  order, tipping: tippingType, tip_rates,
  collect_address, collect_phone, collect_business_name,
  subscription, frequencies,
  data_sharing, defaultShares, session, tribute,
  partner, partner_item, partner_items, goal_partner_item, disable_donation_split,
  partner_is_pledge, foundation,
  payment_methods, creditCardProps, bankProps, paypalProps, deferredProps,
  cryptoProps, stockProps, dafProps, blackbaudCheckoutProps,
  stripeKey, stripeAccount, stripeFonts,
  donationsPath, donationTags, embeddedOn, removeProfileLink,
  token, template, pdt, pledge_id, original_fundraiser_id,
  brand_color, rain_down_hearts,
  social, share_modal, gift_aid_modal_src, multi_org_modal, blank_modal_src,
  emoji_picker_loader_src, widget_instance_token,
  donate_again,
  goal, goal_threshold, showActiveTotals, loaded_as_child,
  postDonationComment, abandoned_cart, monthly_upsell, monthly_upsell_threshold, upsell_image,
  disable_express_checkout,
  matching, employerMatchingProps,
  ...props
}) => {
  const i18n = I18n(props.i18n)

  // Single load of Stripe.js for the whole widget. Memoized on the
  // (key, account, locale) tuple — those don't change at runtime, so
  // this is effectively load-once. Stripe.js dedupes itself if multiple
  // loadStripe calls happen with the same key, but going through one
  // promise here keeps the intent obvious.
  const stripePromise = useMemo(
    () => loadStripe(stripeKey, { stripeAccount, locale: i18n.locale }),
    [ stripeKey, stripeAccount, i18n.locale ],
  )
  const [ panel, setPanel ] = useState('start')
  const [ paymentMethod, setPaymentMethod ] = useState(null)
  const [ finishedDonation, setFinishedDonation ] = useState(null)
  // Drives the ProcessingOverlay. Each payment hook flips this true
  // before the slow part of its submit (after any inline validation
  // that should still render on the step) and back to false on
  // completion. Lives at App level so it keeps the entire panel area
  // covered while the panel collection's height-animates between
  // steps and through ThankYou's mount on success.
  const [ submitInFlight, setSubmitInFlight ] = useState(false)
  // Set when the donor clicks Continue on Start while ECE discovery is
  // still pending and the outcome could change the picker decision —
  // the hybrid policy: only block when there's exactly one non-wallet
  // method known, since that's the only case where waiting affects
  // whether we skip the chooser. A useEffect below resumes the
  // transition once discovery resolves.
  const [ waitingForDiscovery, setWaitingForDiscovery ] = useState(false)

  // Hidden ECE listens for Stripe's onReady to detect Apple Pay /
  // Google Pay availability on the donor's device. Phase 2 of the
  // Stripe port; the actual express-checkout ECE on the dedicated
  // step is independent (Phase 5). Disabled when credit_card isn't
  // configured for this partner: ECE wallets resolve to tokenized
  // card payments, so a partner who's turned off card payments
  // shouldn't get AP/GP either, and there's no point spinning up the
  // hidden ECE only for filterPaymentMethods to hide the rows.
  // `disable_express_checkout` (PWC data) is the per-fundraiser kill
  // switch — same key gates v6's Payment Request Element. With
  // discovery off, both wallet availability flags stay false and
  // filterPaymentMethods never injects the apple_pay / google_pay rows.
  const expressCheckoutEnabled = !!creditCardProps && !disable_express_checkout
  const discovery = useExpressCheckoutDiscovery({ enabled: expressCheckoutEnabled })

  const tipping = useMemo(
    () => parseTipping(tippingType, tip_rates),
    [ tippingType, tip_rates ]
  )

  // Beneficiary + project pickers for the start panel. partnerItem here
  // shadows the original partner_item prop because the donor may pick a
  // sibling org (or the parent goal_partner_item for a "split" donation);
  // downstream (record submission, payment hooks, OrderSummary, contact-
  // step beneficiary) uses the chosen partnerItem. Keep the whole hook
  // return value too so Start.jsx can drive the selectors and validation.
  const beneficiarySelect = useBeneficiarySelect({
    i18n, partner_item, partner_items, goal_partner_item, disable_donation_split,
  })
  const { partnerItem, projectId, project, multiOrg, multiProject } = beneficiarySelect

  // feeConfig is shipped per payment method (each *Props bundle owns its
  // own rates), so resolve to the active method's config. Methods without
  // donor-paid fees (wallet / wire / check / pledge / crypto) resolve to
  // null, which makes calcFees return 0 and hides the cover-costs UI. That zero
  // must not reach the parent page as a covered fee either — see the `fees`
  // guard in utils/messages/completedDonation.js.
  const feeConfig = useMemo(() => {
    switch (paymentMethod) {
    // ECE wallets settle as tokenized cards on the same Stripe account,
    // so they incur the same processing fee as a typed-in card. Without
    // this, the cover-costs row on ExpressCheckout's OrderSummary
    // resolves to fees=0 and disappears even when the partner has
    // cover_costs enabled.
    case 'apple_pay':
    case 'google_pay':
    case 'credit_card': return creditCardProps?.feeConfig
    case 'bank':        return bankProps?.feeConfig
    case 'paypal':
    case 'venmo':       return paypalProps?.feeConfig
    default:            return null
    }
  }, [ paymentMethod, creditCardProps, bankProps, paypalProps ])

  // Canadian receipt + UK Gift Aid sit at the bottom of the Start
  // panel (above Continue) when the active beneficiary supports
  // them. Hook return shape is { available, render, value } — value
  // is what flows into the order draft and into useDonation's
  // collectAddress derivation, since both opt-ins imply we need the
  // donor's full address on Contact.
  const canadianReceipt = useCanadianReceipt({ partnerItem, partner_items })
  const giftAid = useGiftAid({
    i18n, partnerItem, partner_items,
    src: gift_aid_modal_src,
    id: `gift-aid:${widget_instance_token}`,
  })

  const donation = useDonation({
    i18n,
    amount, currencies, presets, min_amount, blank_amount,
    order, tipping, collect_address, feeConfig, subscription,
    canadianReceipt, giftAid,
  })

  // Aggregates the optional metadata block (data sharing /
  // company-contribution / tribute) that sits between the Contact form
  // and the action button on Contact + ExpressCheckout. Mounted once
  // here so each section's state survives panel transitions; passed
  // as one prop into Contact + ExpressCheckout + useRecord.
  const extras = useDonationExtras({
    order, tribute, collect_business_name, postDonationComment,
  })

  // At App level so the captured employer outlives the Start panel.
  const employerMatching = useEmployerMatching(employerMatchingProps)

  // Snippet plugins (window.Pledge). Registered at parse time; this holds their
  // per-plugin state and renders/validates them at the slots each step hosts.
  const plugins = usePlugins({ i18n, currency: donation.currency, session })

  // Impression + outcome tracking for the monthly-upsell panel; see
  // useMonthlyUpsell. Nothing here watches the frequency — the acceptance
  // latches and the donor's current state is read at save time.
  //
  // Must stay above the useRecord call that reads it: same function scope, so a
  // later `const` is a temporal-dead-zone ReferenceError on every render.
  // esbuild doesn't flag that and nothing mounts this component, so a reorder
  // fails silently in every test and breaks the widget outright.
  const monthlyUpsell = useMonthlyUpsell({
    subscription: donation.state.subscription,
    currency: donation.currency.id,
    exponent: donation.currency.exponent,
    amount: donation.giftAmount,
  })

  const record = useRecord({
    i18n, order, partner, partnerItem, projectId, session, pdt, pledge_id,
    donationTags, embeddedOn, donation, donationsPath, extras, monthlyUpsell,
    canadianReceipt, giftAid, employerMatching,
    widget: { token, template },
    // Collected plugin data → order.plugin_data at submit. Closure captures the
    // current donation so a plugin's finalize() sees it. (paymentMethod is
    // intentionally withheld from the plugin context for now — see usePlugins.)
    pluginData: () => plugins.pluginDataParam({ donation }),
  })

  // Crypto flow state — mounted unconditionally (rules-of-hooks), but
  // its rate-polling effect is gated on `enabled` so fiat-only donors
  // at crypto-enabled partners don't trigger background fetches. The
  // hook returns a no-op-safe shape when cryptoProps is null.
  const crypto = useCrypto({
    i18n, donation, record, partner, partnerItem, projectId,
    cryptoProps,
    // Crypto uses the broader supported-fiat list (cryptoProps.currencies),
    // not the partner-configured `currencies` — donors expressing a
    // crypto donation may pick any supported fiat. Falls back to the
    // partner list if cryptoProps is null (crypto isn't configured),
    // which only matters for the lazy reducer init since `enabled` is
    // false in that case and no effects fire.
    currencies: cryptoProps?.currencies || currencies,
    donationTags, embeddedOn,
    originalFundraiserId: original_fundraiser_id,
    setSubmitInFlight,
    enabled: paymentMethod === 'crypto',
  })

  // Shared `finished` handler for all per-method payment hooks. Hooks
  // are responsible for merging serializedDonation + donation state
  // into the result before calling this (see useDeferredPayment /
  // usePaypal). Failures aren't centralized: each hook owns its own
  // base-keyed `errors` state and renders inline on the corresponding
  // payment step (StripePayment / PaypalPayment / DeferredPayment),
  // and each hook drops the overlay itself via setSubmitInFlight(false)
  // before calling setErrors. The payment step stayed mounted under
  // the overlay the whole time, so dropping it lands the donor on a
  // fully-populated form ready for another attempt.
  const finished = result => {
    setSubmitInFlight(false)
    setFinishedDonation(result)
    setPanel('thank_you')
  }

  // Centralized "donate again" reset. Sticky state that the donor
  // probably wants to reuse (amount, contact info, tribute, gift
  // aid, etc.) is left alone. Anything tied to the just-completed
  // donation gets wiped here. Add new resets to this list as more
  // post-donation state lands — single source of truth for what
  // counts as a fresh attempt.
  const donateAgain = () => {
    setFinishedDonation(null)
    setPanel('start')
    record.reset()
    extras.comment.remove()
    crypto.reset()
    blackbaud.reset()
  }

  const paypal = usePaypal({
    i18n, donation, record, paymentMethod, partnerItem,
    paypal: paypalProps?.paypal,
    finished, setSubmitInFlight,
  })

  // Single shared SDK instance for the whole widget. Both the venmo
  // eligibility probe (useVenmo) and the rendered button (PaypalButton)
  // read from this — see usePaypalSdk for why a single loadScript caller
  // is load-bearing. For subscription mode we wait on `paypal.ready`
  // before loading so the SDK is created with its final
  // data-user-id-token in one shot.
  // Skip the load entirely in an unsettleable currency — the SDK 400s on the
  // currency param, which left the button spinning forever.
  const { sdk: paypalSdk, error: paypalSdkError } = usePaypalSdk({
    enabled: !!paypalProps?.paypal && paypal.ready &&
      paypalCurrencySupported(donation.currency.iso_code, paypalProps?.currencies),
    clientId: paypalProps?.paypal?.client_id,
    currency: donation.currency.iso_code,
    idToken: paypal.idToken,
    sandbox: !!paypalProps?.paypal?.sandbox,
  })

  const venmo = useVenmo({ paypalSdk })

  // Blackbaud Checkout's workflow object, mounted here rather than inside the
  // payment panel: panels unmount as the donor navigates (Panel renders
  // `{status && children}`), and the workflow has to outlive that so
  // paymentMode / baseAmount / billingDetails / checkoutSessionId survive a
  // trip back to Contact. Mutually exclusive with Stripe server-side — a
  // Blackbaud fundraiser gets no creditCardProps / bankProps, so nothing else
  // claims credit_card or bank.
  //
  // The workflow is rebuilt against whichever method the picker chose; Card's
  // carries Blackbaud's Apple Pay / Google Pay tabs, so there are no wallet rows.
  const blackbaudEnabled = !!blackbaudCheckoutProps
  const blackbaud = useBlackbaudCheckout({
    i18n, donation, record, partnerItem, paymentMethod,
    blackbaudCheckoutProps,
    finished, setSubmitInFlight,
    enabled: blackbaudEnabled,
  })

  // Recompute when subscription frequency or currency changes — methods
  // restricted by oneTimeOnly / usdOnly drop off and reappear as those
  // conditions toggle. Venmo additionally drops off when the SDK reports
  // it ineligible (browser/device check from useVenmo). Apple Pay and
  // Google Pay are injected by filterPaymentMethods when the discovery
  // ECE has confirmed device support; before discovery resolves they're
  // simply absent from the list.
  // Compute the eligible method list for an ARBITRARY subscription
  // frequency. Factored out of the memo so callers that change the
  // frequency and need the resulting list in the SAME tick (the
  // monthly-upsell confirm) can compute it directly instead of reading
  // the memo, which still reflects the pre-change frequency until React
  // re-renders. Everything else in the filter is frequency-independent.
  const computeMethods = useCallback(subscription => {
    const methods = filterPaymentMethods(payment_methods, {
      subscription,
      currency: donation.currency,
      applePayAvailable:  discovery.applePay,
      googlePayAvailable: discovery.googlePay,
      // Stock + DAF drop off the chooser when the donor's enabled a
      // Canadian receipt or Gift Aid — the third-party settlement
      // pipeline can't deliver a non-US-jurisdiction receipt.
      canadianReceiptEnabled: canadianReceipt.value,
      giftAidEnabled:         giftAid.value,
      // Same for split-donation: stock + DAF init their SDKs with a
      // single EIN and can't fan out across beneficiaries.
      multipleBeneficiaries:  partnerItem.beneficiary.id == '_multiple',
      paypalCurrencies:       paypalProps?.currencies,
    })
    return methods.filter(m => m !== 'venmo' || venmo.eligible)
  }, [
    payment_methods, donation.currency.iso_code, paypalProps?.currencies,
    venmo.eligible, discovery.applePay, discovery.googlePay,
    canadianReceipt.value, giftAid.value,
    partnerItem.beneficiary.id,
  ])

  // Recompute when subscription frequency or currency changes — methods
  // restricted by oneTimeOnly / usdOnly drop off and reappear as those
  // conditions toggle.
  const availablePaymentMethods = useMemo(
    () => computeMethods(donation.state.subscription),
    [ computeMethods, donation.state.subscription ],
  )

  useSimulatedDonation({
    session,
    donation,
    partnerItem,
    onSimulate: result => {
      setFinishedDonation(result)
      setPanel('thank_you')
    },
  })

  useDonationCompletion({
    donation: finishedDonation,
    paymentMethod,
    brandColor: brand_color,
    rainDownHearts: rain_down_hearts,
  })

  // Engagement-level tracking + form-state relay for the
  // abandoned-cart corner dialog. Imported from donate_v6 — the hook
  // is widget-agnostic (document-level input listener; reads
  // data-plg-donor-field attrs off the email + partner-item inputs)
  // and was explicitly written to work for v7's portaled-modal world.
  // See project_abandoned_cart memory for the broader plan.
  useAbandonedCart({
    completed: !!finishedDonation,
    enabled: !!abandoned_cart,
    partnerKey: partner.key,
    defaultExternalId: partner_item.external_id,
    locale: i18n.locale,
  })

  const deferredPayment = useDeferredPayment({
    i18n, donation, record, paymentMethod, partnerItem,
    submitPath: deferredProps?.submitPath,
    finished, setSubmitInFlight,
  })

  // Route a chosen method to its first panel. crypto/stock/daf each run
  // their own combined form (no shared Contact); wallets skip Contact
  // (the sheet supplies billing details); everything else uses Contact.
  // Shared by the picker (handlePaymentMethodSelect) and the
  // single-method skip path (advanceFromStart) so a sole crypto/stock/
  // daf fundraiser routes to its real panel instead of dead-ending on
  // an empty Payment step.
  const routeToMethod = method => {
    setPaymentMethod(method)
    if (method === 'crypto') setPanel('crypto_form')
    else if (method === 'stock') setPanel('stock')
    else if (method === 'daf') setPanel('daf')
    else if (WALLET_METHODS.includes(method)) setPanel('payment')
    else setPanel('contact')
  }

  // Hybrid block: if there's exactly 1 method known and ECE discovery
  // is still pending, the outcome (skip vs. show picker) hinges on
  // whether AP/GP are about to surface. Set the waiting flag and let
  // the resume effect below pick this up once discovery resolves.
  // Multi-method case proceeds straight to the picker — AP/GP simply
  // pop into the list when they arrive.
  //
  // `methods` defaults to the memoized list, but callers that just
  // changed the subscription frequency (monthly-upsell confirm) pass a
  // freshly-computed list so we route on the post-change methods rather
  // than the stale memo.
  const advanceFromStart = (methods = availablePaymentMethods) => {
    if (methods.length === 1) {
      routeToMethod(methods[0])
    } else {
      setPanel('payment_method')
    }
  }

  // The upsell panel sits between Start and the payment chooser/contact
  // step on forward navigation only — back navigation (handlePickerBack
  // below) skips it. Eligibility: small-ish one-time donations to
  // nonprofit beneficiaries on a partner that offers a monthly
  // frequency. Crowdfunds skip; multi-org splits qualify (cover image
  // falls back to default and the body copy substitutes "selected
  // nonprofits" — see Upsell.jsx).
  //
  // The proposed monthly amount is computed once here so we can both
  // gate eligibility on its existence (returns null only when the
  // one-time amount is zero / empty — donor hasn't entered anything
  // valid yet) and pass it through to the panel for the CTA copy +
  // the donation.setAmount call in handleUpsellConfirm. When the
  // one-time is already at the partner's minimum, the proposal
  // equals the one-time; we still show the panel per manager-decided
  // UX so the donor sees the monthly nudge.
  const upsellMonthly = useMemo(() => proposeMonthlyUpsell({
    oneTime: Number(donation.state.amount) || 0,
    minAmount: donation.minAmount?.toNumber?.() || 0,
  }), [ donation.state.amount, donation.minAmount ])

  // Empty string reaches here too (a cleared SP input persists as ''),
  // and Big.js throws on a non-numeric string rather than degrading
  // quietly — fall back to the long-standing $100 default whenever the
  // configured value isn't a usable positive number.
  const monthlyUpsellThreshold = Number(monthly_upsell_threshold) || 100

  const upsellApplicable =
    monthly_upsell
    && donation.state.subscription === 'one_time'
    && donation.usdAmount.lt(monthlyUpsellThreshold)
    && partnerItem.beneficiary.type !== 'Crowdfund'
    && frequencies.includes('monthly')
    && upsellMonthly != null

  // Upsell sits before the payment chooser, so reaching it doesn't
  // need the discovery wait — by the time the donor decides on the
  // upsell screen, AP/GP discovery has had plenty of time to
  // resolve. The discovery-pending wait only kicks in for the
  // direct-skip-to-payment path.
  const handleStartContinue = () => {
    if (upsellApplicable) {
      // Recorded on display, not on accept — a declined offer has to be
      // distinguishable from an offer never shown for the report to have a
      // denominator.
      monthlyUpsell.show({
        originalAmount: donation.giftAmount,
        proposedAmount: upsellMonthly,
      })
      setPanel('upsell')
      return
    }
    if (availablePaymentMethods.length === 1 && !discovery.ready) {
      setWaitingForDiscovery(true)
      return
    }
    advanceFromStart()
  }

  const handleUpsellConfirm = () => {
    // Latches; nothing below can revoke it. The donor's resulting frequency and
    // amount are read fresh on each saveDraft, so a later change to either is
    // recorded as a change rather than as a retracted acceptance.
    monthlyUpsell.accept()
    // Push the proposed monthly amount into donation BEFORE flipping
    // the subscription — setSubscription doesn't touch the amount, so
    // setAmount can run in any order, but doing amount-first keeps the
    // "this is what the new monthly is" intent obvious in the diff.
    donation.setAmount(String(upsellMonthly))
    donation.setSubscription('monthly')
    // availablePaymentMethods still reflects the one-time list this
    // render (the setSubscription above hasn't flushed), so compute the
    // monthly-eligible list explicitly and route on it — otherwise a
    // config whose sole one-time method is oneTimeOnly (e.g. Venmo)
    // would get committed for an unsupported recurring payment.
    advanceFromStart(computeMethods('monthly'))
  }

  const handleUpsellDecline = () => advanceFromStart()
  const handleUpsellBack = () => setPanel('start')

  // Lock the host modal against closing while a submission is in flight,
  // so the donor can't dismiss the dialog mid-payment. submitInFlight is
  // the single all-methods signal (also drives the panel inert +
  // ProcessingOverlay), so this covers every payment path and releases
  // on settle, success or failure.
  useModalLock(submitInFlight)

  // Resume the deferred transition once ECE discovery (or its 20s
  // timeout) resolves.
  useEffect(() => {
    if (!waitingForDiscovery || !discovery.ready) return
    setWaitingForDiscovery(false)
    advanceFromStart()
    // advanceFromStart is intentionally not memoized; we rely on
    // re-running this effect only when waitingForDiscovery or ready
    // change. eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ waitingForDiscovery, discovery.ready ])

  // Picker selection routes through the same map as the single-method
  // skip path (see routeToMethod above for the per-method rationale).
  const handlePaymentMethodSelect = method => routeToMethod(method)

  // Symmetric back: skip the chooser when there's nothing to choose.
  const handlePickerBack = () =>
    setPanel(availablePaymentMethods.length === 1 ? 'start' : 'payment_method')

  const handleDeferredSubmit = fields => {
    setSubmitInFlight(true)
    deferredPayment.sendPayment(fields)
  }

  const { tippingEnabled, coverCostsEnabled } = tipping

  // credit_card and bank are both STRIPE_METHODS, but on a Blackbaud fundraiser
  // they settle through BBMS instead — the guard is what keeps StripePayment
  // from claiming them (and it would, since the buckets key on the method name).
  const isStripeMethod   = STRIPE_METHODS.includes(paymentMethod) && !blackbaudEnabled
  const isBlackbaudMethod = blackbaudEnabled && STRIPE_METHODS.includes(paymentMethod)
  const isWalletMethod   = WALLET_METHODS.includes(paymentMethod)
  const isPaypalMethod   = PAYPAL_METHODS.includes(paymentMethod)
  const isDeferredMethod = DEFERRED_METHODS.includes(paymentMethod)

  // Subscription-only forms (a single non-one_time frequency) show a
  // recurring-cadence badge after every step heading via PageHeader's
  // context — v7's replacement for v6's centered "Monthly donation"
  // heading (donate_v6/Intro.jsx). Keys on the fixed `frequencies`
  // config, not the donor's current selection, so it's computed once.
  const subscriptionOnlyBadge =
    frequencies?.length === 1 && frequencies[0] !== 'one_time'
      ? i18n.t(`subscription_labels.${frequencies[0]}.lineItem`)
      : null

  return (
    <HeadingBadgeProvider value={subscriptionOnlyBadge}>
    <StripeWrapper stripePromise={stripePromise} stripeFonts={stripeFonts}>
      {expressCheckoutEnabled && (
        <DiscoveryECE stripePromise={stripePromise} onReady={discovery.handleReady} />
      )}
      <div className="space-y-4">
        {/* Goal alert lives outside the panel collection so it
            stays visible across step transitions, mirroring v6's
            placement above the panels. Skipped entirely when the
            partner hasn't enabled goal tracking. */}
        {goal && (
          <Goal
            i18n={i18n}
            partner={partner}
            partnerItem={partner_item}
            goalPartnerItem={goal_partner_item}
            loadedAsChild={loaded_as_child}
            goalThreshold={goal_threshold}
            showActiveTotals={showActiveTotals}
            donated={!!finishedDonation}
            blank_modal_src={blank_modal_src}
            widget_instance_token={widget_instance_token}
          />
        )}
        {/* The relative wrapper anchors the absolute-positioned overlay
            to the panel area. inert (empty-string form for React 18;
            React 19+ accepts the boolean directly) suppresses focus,
            pointer events, and a11y on the entire panel collection
            (including the contents of Stripe iframes) while a payment is
            in flight, so the donor can only interact with the overlay's
            "Processing..." state.

            flow-root establishes a block formatting context so
            .widget-content's `margin: -0.5rem` (used for focus-ring
            safe-area inside panels) doesn't collapse upward through
            this wrapper into space-y-4's gap from Goal. Without it,
            the collapsed margin pulled the relative wrapper 8px
            closer to Goal, and the overlay's `-m-3` negative margin
            then bled into Goal's bottom edge. flow-root keeps the
            16px space-y-4 gap intact so the overlay's reach stays
            independent of whatever lands above the panels. */}
        <div className="relative flow-root">
          <div inert={submitInFlight ? '' : undefined}>
            <Panel.Collection panel={panel}>
              <Panel name="start" label={i18n.t('steps.start.heading')}>
                <Start
                  i18n={i18n}
                  donation={donation}
                  plugins={plugins}
                  currencies={currencies}
                  frequencies={frequencies}
                  beneficiarySelect={beneficiarySelect}
                  partnerItems={partner_items}
                  goalPartnerItem={goal_partner_item}
                  disableDonationSplit={disable_donation_split}
                  canadianReceipt={canadianReceipt}
                  giftAid={giftAid}
                  matching={matching}
                  employerMatching={employerMatching}
                  processing={waitingForDiscovery}
                  onContinue={handleStartContinue}
                />
              </Panel>
              <Panel name="upsell" label={i18n.t('upsell.heading')}>
                <Upsell
                  i18n={i18n}
                  donation={donation}
                  monthlyAmount={upsellMonthly}
                  partnerItem={partnerItem}
                  upsellImage={upsell_image}
                  onConfirm={handleUpsellConfirm}
                  onDecline={handleUpsellDecline}
                  onBack={handleUpsellBack}
                />
              </Panel>
              <Panel name="payment_method" label={i18n.t('steps.payment_method.heading')}>
                <PaymentMethodStep
                  i18n={i18n}
                  methods={availablePaymentMethods}
                  onSelect={handlePaymentMethodSelect}
                  onBack={() => setPanel('start')}
                />
              </Panel>
              <Panel name="stock" label={i18n.t('steps.payment.heading')}>
                <StockPayment
                  i18n={i18n}
                  record={record}
                  partner={partner}
                  partnerItem={partnerItem}
                  extras={extras}
                  stockProps={stockProps}
                  finished={finished}
                  setSubmitInFlight={setSubmitInFlight}
                  onBack={handlePickerBack}
                  blank_modal_src={blank_modal_src}
                  emoji_picker_loader_src={emoji_picker_loader_src}
                  widget_instance_token={widget_instance_token}
                />
              </Panel>
              <Panel name="daf" label={i18n.t('steps.payment.heading')}>
                <DafPayment
                  i18n={i18n}
                  record={record}
                  partnerItem={partnerItem}
                  extras={extras}
                  dafProps={dafProps}
                  finished={finished}
                  setSubmitInFlight={setSubmitInFlight}
                  onBack={handlePickerBack}
                  blank_modal_src={blank_modal_src}
                  emoji_picker_loader_src={emoji_picker_loader_src}
                  widget_instance_token={widget_instance_token}
                />
              </Panel>
              <Panel name="crypto_form" label={i18n.t('steps.contact.heading')}>
                <CryptoForm
                  i18n={i18n}
                  employerMatching={employerMatching}
                  donation={donation}
                  plugins={plugins}
                  crypto={crypto}
                  currencies={cryptoProps?.currencies || currencies}
                  accounts={cryptoProps?.accounts || []}
                  min_amount={min_amount}
                  session={session}
                  collect_phone={collect_phone}
                  collect_address={collect_address}
                  data_sharing={data_sharing}
                  beneficiary={partnerItem.beneficiary}
                  partner={partner}
                  partner_is_pledge={partner_is_pledge}
                  foundation={foundation}
                  defaultShares={defaultShares}
                  extras={extras}
                  blank_modal_src={blank_modal_src}
                  emoji_picker_loader_src={emoji_picker_loader_src}
                  widget_instance_token={widget_instance_token}
                  onBack={() => setPanel('payment_method')}
                  onContinue={() => setPanel('crypto_info')}
                />
              </Panel>
              <Panel name="contact" label={i18n.t('steps.contact.heading')}>
                <ContactStep
                  i18n={i18n}
                  employerMatching={employerMatching}
                  donation={donation}
                  plugins={plugins}
                  session={session}
                  collect_phone={collect_phone}
                  collect_address={collect_address}
                  data_sharing={data_sharing}
                  beneficiary={partnerItem.beneficiary}
                  partner={partner}
                  partner_is_pledge={partner_is_pledge}
                  foundation={foundation}
                  defaultShares={defaultShares}
                  extras={extras}
                  blank_modal_src={blank_modal_src}
                  emoji_picker_loader_src={emoji_picker_loader_src}
                  widget_instance_token={widget_instance_token}
                  onBack={handlePickerBack}
                  onContinue={() => setPanel('payment')}
                />
              </Panel>
              <Panel name="payment" label={i18n.t('steps.payment.heading')}>
                {isStripeMethod && (
                  <StripePayment
                    i18n={i18n}
                    donation={donation}
                    record={record}
                    session={session}
                    partnerItem={partnerItem}
                    paymentMethod={paymentMethod}
                    creditCardProps={creditCardProps}
                    bankProps={bankProps}
                    stripePromise={stripePromise}
                    stripeFonts={stripeFonts}
                    tippingEnabled={tippingEnabled}
                    coverCostsEnabled={coverCostsEnabled}
                    pledgeCoversFees={goal_partner_item.pledge_covers_credit_card_fees}
                    finished={finished}
                    setSubmitInFlight={setSubmitInFlight}
                    onBack={() => setPanel('contact')}
                  />
                )}
                {isBlackbaudMethod && (
                  <BlackbaudPayment
                    i18n={i18n}
                    donation={donation}
                    blackbaud={blackbaud}
                    onBack={() => setPanel('contact')}
                  />
                )}
                {isWalletMethod && (
                  <ExpressCheckout
                    i18n={i18n}
                    employerMatching={employerMatching}
                    donation={donation}
                    plugins={plugins}
                    record={record}
                    partnerItem={partnerItem}
                    paymentMethod={paymentMethod}
                    creditCardProps={creditCardProps}
                    collect_phone={collect_phone}
                    collect_address={collect_address}
                    foundation={foundation}
                    partner={partner}
                    partner_is_pledge={partner_is_pledge}
                    data_sharing={data_sharing}
                    defaultShares={defaultShares}
                    extras={extras}
                    blank_modal_src={blank_modal_src}
                    emoji_picker_loader_src={emoji_picker_loader_src}
                    widget_instance_token={widget_instance_token}
                    stripePromise={stripePromise}
                    stripeFonts={stripeFonts}
                    tippingEnabled={tippingEnabled}
                    coverCostsEnabled={coverCostsEnabled}
                    pledgeCoversFees={goal_partner_item.pledge_covers_credit_card_fees}
                    finished={finished}
                    setSubmitInFlight={setSubmitInFlight}
                    onBack={handlePickerBack}
                  />
                )}
                {isPaypalMethod && (
                  <PaypalPayment
                    i18n={i18n}
                    donation={donation}
                    paymentMethod={paymentMethod}
                    partnerItem={partnerItem}
                    project={project}
                    multiOrg={multiOrg}
                    multiProject={multiProject}
                    tippingEnabled={tippingEnabled}
                    coverCostsEnabled={coverCostsEnabled}
                    pledgeCoversFees={goal_partner_item.pledge_covers_credit_card_fees}
                    paypal={paypal}
                    paypalConfig={paypalProps?.paypal}
                    paypalSdk={paypalSdk}
                    paypalSdkError={paypalSdkError}
                    multiOrgModal={multi_org_modal}
                    onBack={() => setPanel('contact')}
                  />
                )}
                {isDeferredMethod && (
                  <DeferredPayment
                    i18n={i18n}
                    donation={donation}
                    paymentMethod={paymentMethod}
                    deferredProps={deferredProps}
                    errors={deferredPayment.errors}
                    onBack={() => setPanel('contact')}
                    onSubmit={handleDeferredSubmit}
                  />
                )}
              </Panel>
              <Panel name="crypto_info" label={i18n.t('crypto_info.heading.pending')}>
                <CryptoInfo
                  i18n={i18n}
                  crypto={crypto}
                  cryptoProps={cryptoProps}
                  donation={donation}
                  partnerItem={partnerItem}
                  finished={finished}
                />
              </Panel>
              <Panel name="thank_you" label={i18n.t('confirmation.heading')}>
                <ThankYou
                  i18n={i18n}
                  donation={finishedDonation}
                  plugins={plugins}
                  comment={extras.comment}
                  donationsPath={donationsPath}
                  removeProfileLink={removeProfileLink}
                  showShare={!!social}
                  shareModal={share_modal}
                  donateAgain={donate_again ? donateAgain : undefined}
                  employerMatching={employerMatching}
                  // Live state: the DAF and crypto results carry no `shares`.
                  shares={donation.shares}
                  blank_modal_src={blank_modal_src}
                  emoji_picker_loader_src={emoji_picker_loader_src}
                  widget_instance_token={widget_instance_token}
                />
              </Panel>
            </Panel.Collection>
          </div>
          <ProcessingOverlay i18n={i18n} show={submitInFlight} />
        </div>
      </div>
    </StripeWrapper>
    </HeadingBadgeProvider>
  )
}

export default App
